import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ElementType,
} from 'react'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { StyledMenu } from 'components/StyledMenu/StyledMenu'
import ChevronUp from 'assets/chevron-up.svg'
import { useLayout } from 'hooks/useLayout'
import { useQueryClient } from '@tanstack/react-query'
import SearchIcon from '@mui/icons-material/Search'
import { useCurrentStateStore } from 'stores/useCurrentStateStore'
import { useSetState } from 'stores/useSetState'
import { useStateListStore } from 'stores/useStateListStore'
import { useLocation } from 'react-router-dom'

const resolveIconComponent = (iconSource: unknown): ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: ElementType }).default
  }

  return iconSource as ElementType
}

export const StateSelector = () => {
  const SearchIconComponent = resolveIconComponent(SearchIcon)
  const { isMobile } = useLayout()
  const qc = useQueryClient()
  const { currentStateName, setCurrentState } = useCurrentStateStore()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const open = Boolean(anchorEl)
  const setPassedState = useSetState((state) => state.setPassedState)
  const { stateList } = useStateListStore()
  const location = useLocation()

  const handleClose = useCallback(() => {
    setAnchorEl(null)
    setSearchTerm('') // clear search when menu closes
  }, [])

  const toggleMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(open ? null : event.currentTarget)
    },
    [open, currentStateName]
  )

  const handleStateSelect = useCallback(
    async (state: { stateName: string | null }) => {
      const selectedState = stateList.find(
        (s) => s.stateName === state.stateName
      )

      // reset data for enroll locations page
      qc.clear()

      if (selectedState) {
        setCurrentState(
          selectedState.stateName ?? 'Unnamed State',
          selectedState.stateId
        )

        // when the user switches States using the select at the top left of the screen, clear the selected State so data can be refreshed and not show the previous State's data
        setPassedState(undefined)

        handleClose()
      }
    },
    [stateList, qc, setCurrentState, handleClose]
  )

  const sortedStates = useMemo(() => {
    return stateList
      .slice()
      .sort((a: { stateName: any }, b: { stateName: any }) =>
        (a.stateName ?? '').localeCompare(b.stateName ?? '')
      )
  }, [stateList])

  // Escape special regex characters in search term
  const escapeRegExp = (s: string) =>
    s.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)

  // filtered accounts for dropdown search (search by account name OR licensedEstablishments.standardName)
  const filteredStates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) {
      // If no search term, return all accounts sorted
      return stateList
        .slice()
        .sort((a, b) => (a.stateName ?? '').localeCompare(b.stateName ?? ''))
    }
    // Escape special regex characters
    const escaped = escapeRegExp(term)
    // Filter accounts based on search term
    return stateList
      .filter((state) => {
        // Check CA name for exact match
        const nameMatch = new RegExp(escaped).exec(
          (state.stateName ?? '').toLowerCase()
        )
        // Return true if either matches
        return Boolean(nameMatch)
      })
      .sort((a, b) => (a.stateName ?? '').localeCompare(b.stateName ?? ''))
  }, [stateList, searchTerm])

  useEffect(() => {
    const firstState = sortedStates?.[0]

    if (firstState && currentStateName === undefined) {
      setCurrentState(
        firstState.stateName ?? 'Unnamed State',
        firstState.stateId
      )
    }
  }, [sortedStates, currentStateName])

  return location.pathname === '/Home' ? (
    <button
      onClick={toggleMenu}
      className="flex flex-row justify-start gap-1 pl-[36px]"
    >
      <Typography
        component={'span'}
        variant="body-2"
        display={['flex', 'flex', 'flex']}
        alignItems="center"
        sx={{
          fontSize: '16px',
          fontWeight: 'bold',
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              display: {
                xs: 'none',
                sm: 'none',
                md: 'none',
                lg: 'block',
              },
            }}
          >
            <span
              style={{
                borderRight: '1px solid #292B2699',
                height: '16px',
                marginRight: '22px',
              }}
            />
          </Box>
        )}
        {currentStateName ?? 'Select State'}
      </Typography>
      <Stack alignItems="center" p={1}>
        {open ? (
          <img
            alt="Closed state select Menu"
            src={ChevronUp}
            width="20"
            height="20"
          />
        ) : (
          <img
            alt="Opened state select Menu"
            src={ChevronUp}
            width="20"
            height="20"
            className="rotate-180"
          />
        )}
      </Stack>
      <StyledMenu
        id="appbar-state-select-menu"
        MenuListProps={{
          'aria-labelledby': 'demo-customized-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: isMobile ? 'center' : 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        }}
        transformOrigin={{
          vertical: isMobile ? 'top' : 'center',
          horizontal: isMobile ? 'center' : 'left',
        }}
        sx={{
          left: '55px',
          marginTop: isMobile ? '0' : '16px',
        }}
      >
        <List
          sx={{
            width: isMobile ? '300px' : '400px',
            maxHeight: '400px',
            overflow: 'auto',
            paddingY: 0,
          }}
        >
          {/* Search box shown at top of dropdown when open */}
          {open && (
            <ListItem
              sx={{
                paddingTop: 2,
                paddingBottom: 1,
              }}
              // prevent clicks inside the menu (search box) from bubbling up and toggling the menu
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <TextField
                fullWidth
                placeholder="Search States"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                // also prevent the native input mousedown from bubbling
                inputProps={{
                  'aria-label': 'search-states',
                  onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
                }}
                // Add search icon at start of input
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                      sx={{ color: 'text.secondary' }}
                    >
                      {SearchIconComponent ? (
                        <SearchIconComponent fontSize="small" />
                      ) : null}
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                sx={{
                  // highlight the border and add focus/hover styles
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'black', // default highlight color
                      borderWidth: 1.5,
                    },
                    '&:hover fieldset': {
                      borderColor: 'black',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'black',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(204,32,39,0.08)',
                    },
                  },
                }}
              />
            </ListItem>
          )}
          {(filteredStates ?? []).map((state, index) => (
            <React.Fragment key={state.stateId.toString()}>
              {index > 0 && <Divider sx={{ width: '100%' }} />}
              <ListItem
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={async () => {
                  if (state) {
                    await handleStateSelect({
                      stateName: state.stateName ?? null,
                    })
                  }
                }}
              >
                <ListItemButton
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor:
                      state.stateName === currentStateName
                        ? 'rgba(204, 32, 39, 0.08)'
                        : 'transparent',
                    paddingLeft: '1px',
                    marginLeft: '-10px',
                    marginRight: '-10px',
                    height: '55px',
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body-2"
                        sx={{
                          fontSize: '16px',
                          color:
                            state.stateName === currentStateName
                              ? '#CC2027'
                              : '#2C2C27',
                          fontWeight:
                            state.stateName === currentStateName
                              ? 'bold'
                              : 'normal',
                        }}
                      >
                        {state.stateName}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </StyledMenu>
    </button>
  ) : (
    <div className="flex flex-row justify-start gap-1 pl-[36px]">
      <Typography
        component={'span'}
        variant="body-2"
        display={['flex', 'flex', 'flex']}
        alignItems="center"
        sx={{
          fontSize: '16px',
          fontWeight: 'bold',
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              display: {
                xs: 'none',
                sm: 'none',
                md: 'none',
                lg: 'block',
              },
            }}
          >
            <span
              style={{
                borderRight: '1px solid #292B2699',
                height: '16px',
                marginRight: '22px',
              }}
            />
          </Box>
        )}
        {currentStateName ?? 'Select State'}
      </Typography>
    </div>
  )
}
